import React, { useEffect, useState } from "react";
import { useUser } from "../../Context/UserContext";
import { default as Header } from "../../Components/Header2/Header2";
import { default as Footer } from "../../Components/Footer/FooterSmall";
import useAPI from "../../Hooks/useAPI";
import ProfileForm from "./ProfileForm";

import "../../Components/Page/Page.css";
import "./Profile.css";
import ContractingRedirect, {
  UserData,
} from "../../Components/Contracting/Contracting";

export interface UserProfileData extends UserData {
  mndaStatus: string;
}

const Profile: React.FC = () => {
  const { user, setUser, hasPermission } = useUser();
  const { updateUser, postUserData, saveContact } = useAPI();
  const [isEditing, setIsEditing] = useState(false);
  const [countrycode, setCountryCode] = useState("");
  const [, setPhoneNoCode] = useState("");

  const [userData, setFormData] = useState<UserProfileData>({
    source: "",
    company: "",
    firstName: "",
    lastName: "",
    phone: "",
    email: "",
    mndaStatus: "",
    title: "",
    id: "",
    entityId: "",
    streetaddress: "",
    streetaddress2: "",
    city: "",
    state: "",
    zip: "",
    contractAdmin: "",
  });

  useEffect(() => {
    // 1. Always initialize with whatever user context is currently available
    if (user) {
      setFormData({
        source: "Contracts<32Gem$",
        company: user.companyName || "",
        firstName: user.firstName || "",
        lastName: user.lastName || "",
        phone: user.phone || "",
        email: user.email || "",
        mndaStatus: user.mndaStatus || "",
        title: user.title || "",
        id: user.id || "",
        entityId: user.entityid || "",
        streetaddress: user.streetaddress || "",
        streetaddress2: user.streetaddress2 || "",
        city: user.city || "",
        state: user.state || "",
        zip: user.zip || "",
        contractAdmin: String(hasPermission("admin_contracts")) || "",
      });
    }
  }, [user?.id]); // runs when user is ready

  //console.log("localUser", user);

  useEffect(() => {
    const fetchAndUpdateUser = async () => {
      if (!user?.id) return;

      try {
        const freshUser = await postUserData(user.email);

        // merge with any local or default data
        const updatedUserData: UserProfileData = {
          source: "Contracts<32Gem$",
          company: freshUser.user.companyName ?? user.companyName ?? "",
          firstName: freshUser.user.firstName ?? user.firstName ?? "",
          lastName: freshUser.user.lastName ?? user.lastName ?? "",
          phone: freshUser.user.phone ?? user.phone ?? "",
          email: freshUser.user.email ?? user.email ?? "",
          mndaStatus: freshUser.user.mndaStatus ?? user.mndaStatus ?? "",
          title: freshUser.user.title ?? user.title ?? "",
          id: freshUser.user.id ?? user.id ?? "",
          entityId: freshUser.user.entityid ?? user.entityid ?? "",
          streetaddress:
            freshUser.user.streetaddress ?? user.streetaddress ?? "",
          streetaddress2:
            freshUser.user.streetaddress2 ?? user.streetaddress2 ?? "",
          city: freshUser.user.city ?? user.city ?? "",
          state: freshUser.user.state ?? user.state ?? "",
          zip: freshUser.user.zip ?? user.zip ?? "",
          contractAdmin:
            freshUser.user.contractadmin ??
            String(hasPermission("admin_contracts")) ??
            "",
        };

        // Update local user context
        if (!isEditing) {
          setUser({
            ...user,
            ...updatedUserData,
          });
        }
        setFormData(updatedUserData);
      } catch (err) {
        console.error("Error refreshing user data:", err);
        // fallback: use existing user context
        const localUserData: UserProfileData = {
          source: "Contracts<32Gem$",
          company: String(user.companyName || ""),
          firstName: String(user.firstName || ""),
          lastName: String(user.lastName || ""),
          phone: String(user.phone || ""),
          email: String(user.email || ""),
          mndaStatus: String(user.mndaStatus || ""),
          title: String(user.title || ""),
          id: String(user.id || ""),
          entityId: String(user.entityid || ""),
          streetaddress: String(user.streetaddress || ""),
          streetaddress2: String(user.streetaddress2 || ""),
          city: String(user.city || ""),
          state: String(user.state || ""),
          zip: String(user.zip || ""),
          contractAdmin: String(hasPermission("admin_contracts")),
        };
        setFormData(localUserData);
      }
    };

    fetchAndUpdateUser();
  }, [user!.id]);

  const formatPhoneNumber = (phoneNumber: string): string => {
    // Remove all non-digit characters
    const cleaned = phoneNumber.replace(/\D/g, "");

    // If the number is 11 digits and starts with 1 (US number)
    if (cleaned.length === 11 && cleaned.startsWith("1")) {
      const usNumber = cleaned.slice(1);
      return `(${usNumber.slice(0, 3)}) ${usNumber.slice(
        3,
        6,
      )}-${usNumber.slice(6)}`;
    }

    // If the number is 10 digits (US number without country code)
    if (cleaned.length === 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(
        6,
      )}`;
    }

    // For international numbers, format as +XX XXX XXX XXXX
    // This handles numbers with country codes of varying lengths
    const countryCode = cleaned.slice(0, cleaned.length - 10);
    const nationalNumber = cleaned.slice(cleaned.length - 10);

    return `+${countryCode} ${nationalNumber.slice(
      0,
      3,
    )} ${nationalNumber.slice(3, 6)} ${nationalNumber.slice(6)}`;
  };

  const handleSubmit = async (formData: any) => {
    if (!user?.id) {
      throw new Error("User ID not found");
    }

    await updateUser(
      user.id,
      {
        ...formData,
        email: user.email,
      },
      user.id,
    );
    // use the latest input value, not user.phone
    const phoneInput = formData.phone?.trim() || "";

    let derivedCountryCode = countrycode;
    let derivedPhoneNoCode = phoneInput;

    if (phoneInput) {
      const cleaned = phoneInput.replace(/\D/g, "");

      if (cleaned.length === 11 && cleaned.startsWith("1")) {
        derivedCountryCode = cleaned[0]; // '1'
        derivedPhoneNoCode = cleaned.slice(1); // remove country code
      } else {
        derivedPhoneNoCode = cleaned; // just digits
      }
    }

    // update UI state if needed
    setCountryCode(derivedCountryCode);
    setPhoneNoCode(derivedPhoneNoCode);

    const response = await saveContact(
      {
        ...formData,
        email: user.email,
        countrycode: derivedCountryCode,
        phonenocode: derivedPhoneNoCode,
        streetaddress1: user.streetaddress,
        agreeToTerms: true,
        datasource: "",
        ipaddress: "",
      },
      false,
      user.cidn,
    );

    // Update local user context
    setUser({
      ...user,
      ...formData,
    });

    setIsEditing(false);
  };

  if (!user) {
    return <div>No user data available.</div>;
  }

  return (
    <div className="generalPage">
      <Header />
      <div className="pageContainer">
        <div className="profile-container">
          <div className="profile-header">
            <h2>User Profile</h2>
            <button
              className={`edit-button ${isEditing ? "cancel" : ""}`}
              onClick={() => setIsEditing(!isEditing)}
            >
              {isEditing ? "Cancel" : "Edit Profile"}
            </button>
          </div>

          {isEditing ? (
            <ProfileForm
              user={user}
              onSubmit={handleSubmit}
              onCancel={() => setIsEditing(false)}
            />
          ) : (
            <table className="profile-table">
              <tbody>
                <tr>
                  <th>First Name</th>
                  <td id="profile-firstName">{user.firstName}</td>
                </tr>
                {user.middleName && (
                  <tr>
                    <th>Middle Name</th>
                    <td id="profile-middleName">{user.middleName}</td>
                  </tr>
                )}
                <tr>
                  <th>Last Name</th>
                  <td id="profile-lastName">{user.lastName}</td>
                </tr>
                <tr>
                  <th>Email</th>
                  <td id="email">{user.email}</td>
                </tr>
                <tr>
                  <th>
                    {/* {user.mndaStatus === "No" ||
                    user.mndaStatus?.trim() === "Started" ? ( */}
                    <ContractingRedirect
                      source={userData.source}
                      company={userData.company}
                      firstName={userData.firstName}
                      lastName={userData.lastName}
                      phone={userData.phone}
                      email={userData.email}
                      title={userData.title}
                      id={userData.id}
                      entityId={userData.entityId}
                      streetaddress={userData.streetaddress}
                      streetaddress2={userData.streetaddress2}
                      city={userData.city}
                      state={userData.state}
                      zip={userData.zip}
                      contractAdmin={userData.contractAdmin}
                    />
                    {/* ) : ( */}
                    {/* "Contract" */}
                    {/* )} */}
                  </th>
                  <td id="agreements">
                    <div>
                      <table className="contractstatus-table">
                        <thead className="contractstatus-header"></thead>
                        <tbody>
                          {!user?.mndaStatus ||
                          Object.keys(user.mndaStatus).length === 0 ? (
                            <tr className="contractstatus-row">
                              <td className="contractstatus-row">None</td>
                            </tr>
                          ) : (
                            Object.entries(user!.mndaStatus!).map(
                              ([type, status]) => (
                                <tr className="contractstatus-row" key={type}>
                                  <td className="contractstatus-row">
                                    {status}
                                  </td>
                                </tr>
                              ),
                            )
                          )}
                        </tbody>
                      </table>
                    </div>
                  </td>
                </tr>
                {user.streetaddress && (
                  <tr>
                    <th>Addresss</th>
                    <td id="streetaddress">{user.streetaddress}</td>
                  </tr>
                )}
                {user.streetaddress2 && (
                  <tr>
                    <th>Address 2</th>
                    <td id="streetaddress2">{user.streetaddress2}</td>
                  </tr>
                )}
                {user.city && (
                  <tr>
                    <th>City</th>
                    <td id="city">{user.city}</td>
                  </tr>
                )}
                {user.state && (
                  <tr>
                    <th>State</th>
                    <td id="state">{user.state}</td>
                  </tr>
                )}
                {user.zip && (
                  <tr>
                    <th>Zip</th>
                    <td id="zip">{user.zip}</td>
                  </tr>
                )}
                {user.country && (
                  <tr>
                    <th>Country</th>
                    <td id="country">{user.country}</td>
                  </tr>
                )}
                {user.phone && (
                  <tr>
                    <th>Phone</th>
                    <td id="phone">{formatPhoneNumber(user.phone)}</td>
                  </tr>
                )}
                {user.companyName && (
                  <tr>
                    <th>Company Name</th>
                    <td id="company">{user.companyName}</td>
                  </tr>
                )}
                {user.companyType && (
                  <tr>
                    <th>Company Type</th>
                    <td id="companyType">{user.companyType}</td>
                  </tr>
                )}
                {user.title && (
                  <tr>
                    <th>Title</th>
                    <td id="title">{user.title}</td>
                  </tr>
                )}
                <tr>
                  <th>Professions</th>
                  <td id="profession">
                    {user.profession?.join(", ")}
                    {user.professionOther &&
                      user.profession?.includes("Other") && (
                        <span> ({user.professionOther})</span>
                      )}
                  </td>
                </tr>
                <tr>
                  <th>Interests</th>
                  <td id="interests">
                    {user.interests?.join(", ")}
                    {user.interestsOther &&
                      user.interests?.includes("Other") && (
                        <span> ({user.interestsOther})</span>
                      )}
                  </td>
                </tr>
              </tbody>
            </table>
          )}
          <br/>
          <a
                    href="/documents/ICEGEMS_Terms-and-Conditions.pdf"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="terms-link"
                  >
                    Terms & Conditions
                    </a>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Profile;
